import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	inject,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS_PCDS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { Validators } from '@angular/forms';
import { DocumentExporter } from '@utils/document-exporter';
import * as pdfseira from '@utils/pdf-seira';
import html2canvas from 'html2canvas';
import { RelatorioVariavelEstacao } from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/pages/variaveis/interfaces/estacao-ultimas-medicoes';

import { DateTimeUtils } from '@utils/datetime-util';
import {
	baixarEAbrirPDF,
	numberToBrNumberTables,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { VariaveisService } from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/services/variaveis.service';
import { ToastrService } from 'ngx-toastr';
import {
	criarCanvasInterpolacao,
	InterpolacaoCanvasBounds,
} from '@utils/interpolacao/interpolacao';
import {
	getFormattedVariavel,
	getLabelCard,
	getUnidade,
} from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/pages/variaveis/interfaces/variaveis';
import { FeatureCollection, Polygon } from 'geojson';
import { Position } from 'pdfmake/interfaces';
import * as Leaflet from 'leaflet';
import { MapaInterpolacaoComponent } from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/pages/variaveis/componentes/mapa-interpolacao/mapa-interpolacao.component';
import { OpcaoPagina } from '@componentes/public-page-options/public-page-options.component';
import { Subject, takeUntil } from 'rxjs';
import { RelatoriosService } from '../../../services';
import { corrigeDuplicacaoNome } from '../../../utils';
import * as ExcelTable from 'mr-excel';
import { ValuesVariaveis } from '../../../submodulos/monitoramento/interfaces/estacao-monitorada';
import { bootstrapLarguraSM } from '@utils/constants';
import { alinharDado } from '../../../../../../../shared/utils/index';
import { format } from 'date-fns';

@Component({
	selector: 'seira-mapa-interpolacao-estacoes-pcds',
	templateUrl: './mapa-interpolacao-estacoes-pcds.component.html',
	styleUrls: ['./mapa-interpolacao-estacoes-pcds.component.scss'],
})
export class MapaInterpolacaoEstacoesPCDSComponent
	implements InstanciaRelatorio, OnInit, OnDestroy, AfterViewInit
{
	map!: Leaflet.Map;
	inputs = inject(INPUTS_RELATORIOS_PCDS);
	isVariavelPrecipitacao = false;
	variavel = '';
	unidade = '';
	mapaNome = '';
	periodoTitulo = '';
	valores: RelatorioVariavelEstacao[] = [];
	medicoes: RelatorioVariavelEstacao[] = [];
	interpolacao?: InterpolacaoCanvasBounds;
	regiao?: Position[][];
	_destroyed = new Subject();
	@ViewChild('legenda') legendaMapaMobile!: TemplateRef<any>;
	@ViewChild(MapaInterpolacaoComponent) mapa!: MapaInterpolacaoComponent;
	opcoesDaPagina: OpcaoPagina<any>[] = [];
	private _carregando = false;

	descricaoRelatorio =
		'Define-se como variável meteorológica interpolada a quantidade total dessa variável registrada em um local ou região específica, interpolada ao longo de um período.';

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			activeFakeLoading: true,
			onClick: () => {
				if (this.mapa) {
					this.mapa.resetMapView();
				}
				setTimeout(() => {
					return this.exportarPDF();
				}, 1000);
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];

	constructor(
		private httpClient: HttpClient,
		private variavelService: VariaveisService,
		private readonly toastr: ToastrService,
		private cdr: ChangeDetectorRef,
		private relatoriosService: RelatoriosService
	) {}

	ngOnInit(): void {
		this.initialValues();
		this.setValidatorsByJanelaTempo(
			this.inputs.form.get(FormularioRelatorio.JANELA_TEMPO)?.value
		);
		this.setValidators();
	}

	ngAfterViewInit() {
		this.adicionarOpcoesDaPagina();
	}

	ngOnDestroy(): void {
		this.inputs.form.get(FormularioRelatorio.JANELA_TEMPO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.VARIAVEL_MAPA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this._destroyed.next(undefined);
	}

	setupMap(mapa: Leaflet.Map) {
		this.map = mapa;
		this.cdr.detectChanges();
	}

	public get carregando() {
		return this._carregando;
	}

	public set carregando(valor: boolean) {
		this._carregando = valor;
		this.inputs.setLoading(valor);
	}

	setValidatorsByJanelaTempo(janelaTempo: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.DATA_INICIO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.DATA_FIM);

		if (janelaTempo === 'Personalizada') {
			this.inputs.form
				.get(FormularioRelatorio.DATA_INICIO)
				?.setValidators(Validators.required);
			this.inputs.form
				.get(FormularioRelatorio.DATA_FIM)
				?.setValidators(Validators.required);
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.JANELA_TEMPO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByJanelaTempo(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.JANELA_TEMPO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.VARIAVEL_MAPA)
			?.setValidators(Validators.required);
	}

	handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	initialValues() {
		const pbGeoJsonObservable = this.httpClient.get<FeatureCollection>(
			'assets/geoJson/pb-geo.json'
		);
		pbGeoJsonObservable.subscribe(value => {
			this.regiao = (<Polygon>value.features[0].geometry)
				.coordinates as Position[][];
		});
	}

	gerarRelatorio(): void {
		const variavel = this.inputs.form.get('variavelMapa')?.value;
		const janelaTempo = this.inputs.form.get('janelaTempo')?.value;
		this.isVariavelPrecipitacao = variavel === ValuesVariaveis.PRECIPITACAO;
		this.unidade = getUnidade(variavel);
		this.variavel = this.getVariavelFromValor(variavel);
		this.mapaNome = getFormattedVariavel(variavel);
		this.periodoTitulo = this.getPeriodoTitulo();

		this.carregando = true;
		this.inputs.setLoading(true);

		if (janelaTempo === 'Personalizada') {
			const diaInicio = this.inputs.form.get('dataInicio')?.value;
			const diaFim = this.inputs.form.get('dataFim')?.value;

			this.variavelService
				.medicoesPersonalizada({ diaInicio, diaFim }, variavel)
				.subscribe({
					next: resp => {
						const collator = new Intl.Collator('pt-BR', {
							sensitivity: 'base',
							numeric: true,
						});

						const dadosOrdenados = [...resp].sort((a, b) =>
							collator.compare(a.municipio, b.municipio)
						);
						this.medicoes = dadosOrdenados;

						this.relatoriosService.atualizaVisibilidadeBotoesExportacao(resp);
					},
					error: (err: HttpErrorResponse) => {
						this.toastr.error(err.message, 'Erro ao gerar mapa');
						this.carregando = false;
						this.inputs.setLoading(false);
					},
					complete: () => {
						this.carregando = false;
						this.inputs.setLoading(false);
						this.interpolarValores();
					},
				});
		} else {
			this.variavelService.getUltimasMedicoes(janelaTempo, variavel).subscribe({
				next: resp => {
					const collator = new Intl.Collator('pt-BR', {
						sensitivity: 'base',
						numeric: true,
					});

					const dadosOrdenados = [...resp].sort((a, b) =>
						collator.compare(a.municipio, b.municipio)
					);
					this.medicoes = dadosOrdenados;
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						dadosOrdenados
					);
				},
				error: (err: HttpErrorResponse) => {
					this.toastr.error(err.message, 'Erro ao gerar mapa');
					this.carregando = false;
					this.inputs.setLoading(false);
				},
				complete: () => {
					this.interpolarValores();
					this.carregando = false;
					this.inputs.setLoading(false);
				},
			});
		}
	}

	interpolarValores() {
		this.carregando = true;
		if (this.medicoes.length > 0) {
			this.criarImagemInterpolacao().then(interpolacao => {
				this.interpolacao = interpolacao as InterpolacaoCanvasBounds;
				this.carregando = false;
			});
		} else {
			this.carregando = false;
			this.interpolacao = undefined;
		}
	}

	criarImagemInterpolacao() {
		const variavel = this.inputs.form.get('variavelMapa')?.getRawValue();

		const valores: any = this.medicoes.map(value => {
			const valorTipoPCD = value.tipoPCD;
			return {
				lat: value.latitude,
				lng: value.longitude,
				value: value.valorAgrupado,
				municipio: value.municipio,
				valorMax: value.valorMax,
				valorMin: value.valorMin,
				nomePosto: value.nomePosto,
				tipoPCD: this.labelTipoPcd(valorTipoPCD),
			};
		});
		return new Promise((resolve, reject) => {
			const interpolacao = criarCanvasInterpolacao(
				valores,
				variavel,
				this.regiao as number[][][],
				0.01
			);
			if (interpolacao === undefined || interpolacao === null) {
				reject();
			}
			resolve(interpolacao);
		});
	}

	getPeriodoTitulo() {
		const janelaTempo = this.inputs.form.get('janelaTempo')?.value;
		let periodo;

		switch (janelaTempo) {
			case '6':
				periodo = DateTimeUtils.getPeriodoPorHoras(6);
				break;
			case '24':
				periodo = DateTimeUtils.getPeriodoPorHoras(24);
				break;
			case '48':
				periodo = DateTimeUtils.getPeriodoPorHoras(48);
				break;
			case '72':
				periodo = DateTimeUtils.getPeriodoPorHoras(72);
				break;
			case 'Personalizada':
				return this.formataDataPersonalizada(
					this.inputs.form.get('dataInicio')?.value,
					this.inputs.form.get('dataFim')?.value
				);
			default:
				periodo = DateTimeUtils.getPeriodoPorHoras(6);
		}

		const dataInicioFormatada = DateTimeUtils.formatarISOParaFormato24hrs(
			periodo.diaInicio.toISOString()
		);
		const dataFimFormatada = DateTimeUtils.formatarISOParaFormato24hrs(
			periodo.diaFim.toISOString()
		);

		return `${dataInicioFormatada} a ${dataFimFormatada}`;
	}

	getVariavelFromValor(variavel: string): string {
		return getLabelCard(variavel);
	}

	getDadosTabelaParaExportacao(
		dados: RelatorioVariavelEstacao[],
		isPdf: boolean
	) {
		const tableData: any[][] = [];
		const colunas = [
			{ text: 'Município/Posto', fillColor: '#DCDCDC', alignment: 'center' },
			{ text: 'Tipo de PCD', fillColor: '#DCDCDC', alignment: 'center' },
			{ text: 'Data', fillColor: '#DCDCDC', alignment: 'center' },
		];

		if (!this.isVariavelPrecipitacao) {
			colunas.push({
				text: `Valor mínimo (${this.unidade})`,
				fillColor: '#DCDCDC',
				alignment: 'center',
			});
			colunas.push({
				text: `Valor máximo (${this.unidade})`,
				fillColor: '#DCDCDC',
				alignment: 'center',
			});
			colunas.push({
				text: `Média (${this.unidade})`,
				fillColor: '#DCDCDC',
				alignment: 'center',
			});
		} else {
			colunas.push({
				text: `Total observado (${this.unidade})`,
				fillColor: '#DCDCDC',
				alignment: 'center',
			});
		}

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		dados.forEach((item: RelatorioVariavelEstacao) => {
			const rowData = [
				corrigeDuplicacaoNome(`${item.municipio}/${item.nomePosto}`),
				alinharDado(this.labelTipoPcd(item.tipoPCD), isPdf),
				alinharDado(
					format(new Date(item.dataColeta!), 'dd/MM/yyyy') ?? '',
					isPdf
				),
			];

			if (!this.isVariavelPrecipitacao) {
				rowData.push(
					alinharDado(numberToBrNumberTables(item.valorMin, 2), isPdf)
				);
				rowData.push(
					alinharDado(numberToBrNumberTables(item.valorMax, 2), isPdf)
				);
			}
			rowData.push(
				alinharDado(numberToBrNumberTables(item.valorAgrupado, 2), isPdf)
			);

			tableData.push(rowData);
		});

		return tableData;
	}

	retornarPluviometriaInterpoladaParaXLSX(dados: RelatorioVariavelEstacao[]) {
		const tableData: any[] = [];

		dados.forEach(item => {
			const rowData: any = {
				municipioPosto: corrigeDuplicacaoNome(
					`${item.municipio}/${item.nomePosto}`
				),
				tipoPCD: this.labelTipoPcd(item.tipoPCD),
				data: format(new Date(item.dataColeta!), 'dd/MM/yyyy') ?? '',
			};

			if (!this.isVariavelPrecipitacao) {
				rowData.valorMinimo = numberToBrNumberTables(item.valorMin, 2);
				rowData.valorMaximo = numberToBrNumberTables(item.valorMax, 2);
				rowData.media = numberToBrNumberTables(item.valorAgrupado, 2);
			} else {
				rowData.totalObservado = numberToBrNumberTables(item.valorAgrupado, 2);
			}

			tableData.push(rowData);
		});

		return tableData;
	}

	async getMapImage() {
		const elementoHtml = document.getElementById('mapa-legenda');
		if (elementoHtml) {
			const canva = await html2canvas(elementoHtml, {
				useCORS: true,
				allowTaint: true,
				logging: false,
				scale: 2,
			});

			return canva.toDataURL('image/png', 1);
		} else {
			return null;
		}
	}

	async exportarPDF() {
		const orientacao = !this.isVariavelPrecipitacao ? 'landscape' : 'portrait';

		const documentDefinition: any = await pdfseira.documentDefinitions(
			orientacao
		);
		const img = await this.getMapImage();

		documentDefinition.content.push([
			{
				text: `Variável meteorológica interpolada - ${this.variavel} - ${this.periodoTitulo}`,
				alignment: 'center',
				margin: [0, 15, 5, 15],
			},
			{
				image: img,
				width: window.innerWidth > bootstrapLarguraSM ? 400 : 200,
				height: window.innerWidth > bootstrapLarguraSM ? 300 : undefined,
				margin: [0, 15, 5, 15],
				alignment: 'center',
			},
		]);

		const tableData = this.getDadosTabelaParaExportacao(this.medicoes, true);
		const widths = this.isVariavelPrecipitacao
			? ['25%', '25%', '25%', '25%']
			: ['*', '*', '*', '16%', '16%', '*'];

		documentDefinition.content.push({
			table: {
				widths: widths,
				body: tableData,
				layout: {
					noWrap: false,
				},
			},
			marginLeft: 15,
			marginRight: 15,
		});

		baixarEAbrirPDF(
			`Variável meteorológica interpolada - ${this.variavel} - ${this.periodoTitulo}`,
			documentDefinition
		);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(this.medicoes, false);

		let txtData = '';
		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(
			txtData,
			`Variável meteorológica interpolada - ${this.variavel} - ${this.periodoTitulo}`
		);
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(this.medicoes, false);
		DocumentExporter.gerarCSV(
			tableData,
			`Variável meteorológica interpolada - ${this.variavel} - ${this.periodoTitulo}`
		);
	}

	async exportarXLSX() {
		const nomeArquivo = `Variável meteorológica interpolada - ${this.variavel} - ${this.periodoTitulo}`;

		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};

		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const headers = [
			{ label: 'municipioPosto', text: 'Município/Posto', size: 24 },
			{ label: 'tipoPCD', text: 'Tipo de PCD', size: 24 },
			{ label: 'data', text: 'Data', size: 24 },
		];

		const columns: any[] = [{ key: 'municipioPosto' }, { key: 'tipoPCD' }];

		if (!this.isVariavelPrecipitacao) {
			headers.push({
				label: 'valorMinimo',
				text: `Valor mínimo (${this.unidade})`,
				size: 24,
			});
			headers.push({
				label: 'valorMaximo',
				text: `Valor máximo (${this.unidade})`,
				size: 24,
			});
			headers.push({
				label: 'media',
				text: `Média (${this.unidade})`,
				size: 24,
			});
			columns.push({ key: 'valorMinimo', style: { numFmt: '0.0' } });
			columns.push({ key: 'valorMaximo', style: { numFmt: '0.0' } });
			columns.push({ key: 'media', style: { numFmt: '0.0' } });
		} else {
			headers.push({
				label: 'totalObservado',
				text: 'Total observado (mm)',
				size: 24,
			});
			columns.push({ key: 'totalObservado', style: { numFmt: '0.0' } });
		}

		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number,
						fromHeader: boolean,
						styleKeys: string[]
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex > 0) {
								return 'rowStyleCenter';
							} else {
								return 'rowStyle';
							}
						}
					},
					headers: headers,
					data: this.retornarPluviometriaInterpoladaParaXLSX(this.medicoes),
					columns: columns,
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	labelTipoPcd(tipo: string | null) {
		if (tipo === 'METEOROLOGICA') {
			return 'Meteorológica';
		} else if (tipo === 'AGROMETEOROLOGICA') {
			return 'Agrometeorológica';
		}
		return null;
	}

	adicionarOpcoesDaPagina() {
		this.opcoesDaPagina = [
			{
				label: 'Legenda',
				template: this.legendaMapaMobile,
			},
		];
	}
	formataDataPersonalizada(dataInicio: Date, dataFim: Date) {
		const hoje = new Date();
		const ehHoje = (data: Date) =>
			data.getDate() === hoje.getDate() &&
			data.getMonth() === hoje.getMonth() &&
			data.getFullYear() === hoje.getFullYear();

		const inicioAjustado = new Date(dataInicio);
		inicioAjustado.setHours(0 - 3, 0, 0, 0);
		const fimAjustado = new Date(dataFim);
		if (!ehHoje(dataFim)) {
			fimAjustado.setHours(23 - 3, 59, 59, 999);
		} else {
			fimAjustado.setHours(
				hoje.getHours() - 3,
				hoje.getMinutes(),
				hoje.getSeconds(),
				hoje.getMilliseconds()
			);
		}

		const dataInicioFormatada = DateTimeUtils.formatarISOParaFormato24hrs(
			inicioAjustado.toISOString()
		);
		const dataFimFormatada = DateTimeUtils.formatarISOParaFormato24hrs(
			fimAjustado.toISOString()
		);

		return `${dataInicioFormatada} a ${dataFimFormatada}`;
	}
}
